import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { GeneralService } from 'src/app/services/general.service';
import { VendorService } from 'src/app/services/Vendor.service';
import { CommonCalculatorService } from 'src/app/Shared/commonCalculators.service';
import { ECountrycode } from 'src/app/Shared/enum';
import { PhoneFormatPipe } from 'src/app/Shared/phone-format.pipe';
import { ANGULAR_MODULES, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ops-contact-person',
  imports: [ANGULAR_MODULES, SYNCFUSION_MODULES, WordReplacePipe, PhoneFormatPipe],
  standalone: true,
  templateUrl: './ops-contact-person.html',
  styleUrl: './ops-contact-person.scss'
})

export class OpsContactPerson implements OnInit, OnDestroy {

  @Output() close = new EventEmitter<void>();
  @Input() vendorId!: number;
  private destroy$ = new Subject<void>();

  vendorContactForm!: FormGroup;
  viewContactPerson: boolean = true;
  vendorContactData: any[] = [];
  vendorInfo: any;
  vendorMarketID = '';
  vendorName = '';
  telephone = '';
  Email = '';
  address = '';
  states: any[] = [];
  countries: any[] = [];
  statesAvailable: boolean = true;
  logoPreviewUrl: string = '../assets/images/NoPhoto.svg';
  isEditMode: boolean = false;
  isProfileImageUpdated: boolean = false;
  public PhoneNumberMask: String = '(000)000-0000';
  selectedCountryCode: ECountrycode;
  ECountrycode = ECountrycode;

  constructor(
    private generalService: GeneralService,
    private vendorService: VendorService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private commonCalculatorService: CommonCalculatorService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadCountries();
    this.getVendorInfo();
    this.loadVendorContacts();
  }

  initForm(): void {
    this.vendorContactForm = this.fb.group({
      ContactPersonID: [null],
      VendorId: [0],
      ContactName: [''],
      FirstName: ['', [Validators.required]],
      MiddleName: [''],
      LastName: [''],
      Address: ['', [Validators.required]],
      ZipCode: ['', [Validators.required]],
      TelePhone: ['', [Validators.required]],
      EmailAddr: ['', [Validators.pattern('^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$'), Validators.required]],
      PhotoFile: [''],
      ProfileImageName: [''],
      IsAuthorised: [false],
      City: ['', [Validators.required]],
      StateCode: ['', [Validators.required]],
      State: [''],
      CountryCode: [''],
      DeleteFlag: [false],
      ImgDeleteFlag: [false]
    });
  }

  loadCountries(): void {
    this.generalService.getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res && res.length > 0) {
            this.countries = res;
          } else {
            this.countries = [{ Code: ECountrycode.US, Name: 'US' }];
          }
        },
        error: (err) => {
          console.error('Failed to load countries', err);
          this.countries = [{ Code: ECountrycode.US, Name: 'US' }];
        }
      });
  }

  loadStates(countryCode: number): void {
    this.generalService.getStates(countryCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.states = res;
          this.statesAvailable = res && res.length > 0;
        },
        error: (err) => {
          console.error('Failed to load states for country ' + countryCode, err);
          this.states = [];
          this.statesAvailable = false;
          this.vendorContactForm.patchValue({ StateCode: '' });
        }
      });
  }

  onCountryChange(event: Event): void {
    const selectEl = event.target as HTMLSelectElement;
    const countryCode = Number(selectEl.value);
    this.selectedCountryCode = countryCode;
    if (countryCode) {
      this.loadStates(countryCode);
      this.vendorContactForm.patchValue({ StateCode: null });
      this.setZipCodeValidator(countryCode);
    }
  }

  getVendorInfo() {
    this.vendorService.GetVendorById(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.vendorInfo = res;
          this.vendorMarketID = res.MarketVendorId;
          this.vendorName = res.VendorName;
          this.telephone = res.Telephone;
          this.Email = res.Email;
          this.address = res.Address;
        },
        error: (err) => console.error('Failed to load vendor info', err)
      });
  }

  loadVendorContacts(): void {
    this.vendorService.getVendorContactPersons(this.vendorId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.vendorContactData = res;
      },
      error: (err) => {
        console.error('Error fetching vendor contacts:', err);
      }
    });
  }

  onContactRowSelected(args: any): void {
    const rowData = args.data;
    if (!rowData) return;

    this.isEditMode = true;
    let countryCode = rowData.CountryCode;
    if (!countryCode) {
      countryCode = ECountrycode.US;
      this.vendorContactForm.patchValue({ CountryCode: countryCode });
    }
    this.loadStates(countryCode);
    this.selectedCountryCode = countryCode;
    this.setZipCodeValidator(countryCode);
    this.logoPreviewUrl = rowData.PhotoFile && rowData.PhotoFile.trim() !== ''
      ? rowData.PhotoFile
      : '../assets/images/NoPhoto.svg';

    this.vendorContactForm.patchValue({
      ContactPersonID: rowData.ContactPersonID,
      VendorId: rowData.VendorId,
      ContactName: rowData.ContactName,
      FirstName: rowData.FirstName,
      MiddleName: rowData.MiddleName,
      LastName: rowData.LastName,
      Address: rowData.Address,
      ZipCode: rowData.ZipCode,
      TelePhone: rowData.TelePhone,
      EmailAddr: rowData.EmailAddr,
      PhotoFile: rowData.PhotoFile,
      IsAuthorised: rowData.IsAuthorised,
      City: rowData.City,
      StateCode: rowData.StateCode,
      CountryCode: rowData.CountryCode,
      DeleteFlag: rowData.DeleteFlag,
    });
    this.viewContactPerson = false;
  }

  addContactImageClick() {
    const el = document.getElementById('contactImageInput') as HTMLElement;
    if (el) {
      el.click();
    }
  }

  addContactPersonImage(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPG and PNG files are allowed.');
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (file.type === 'image/jpeg') {
          // Already JPG → no conversion needed
          const base64Content = (reader.result as string).split(',')[1];
          this.logoPreviewUrl = reader.result as string;
          this.vendorContactForm.patchValue({
            ProfileImageName: file.name,
            PhotoFile: base64Content,
            ImgDeleteFlag: false
          });
          this.isProfileImageUpdated = true;
          input.value = '';
        } else {
          // Convert PNG → JPG
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0);

            const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.9); // JPG output
            const base64Content = jpgDataUrl.split(',')[1];
            this.logoPreviewUrl = jpgDataUrl;

            this.vendorContactForm.patchValue({
              ProfileImageName: file.name.replace(/\.[^/.]+$/, '') + '.jpg',
              PhotoFile: base64Content,
              ImgDeleteFlag: false
            });
            this.isProfileImageUpdated = true;
            input.value = '';
          };
          img.src = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  removeContactPersonImage() {
    this.vendorContactForm.patchValue({
      ProfileImageName: '',
      PhotoFile: null,
      ImgDeleteFlag: true
    });
    this.logoPreviewUrl = '../assets/images/NoPhoto.svg';
    const fileInput = document.getElementById('contactImageInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  saveVendorContactPerson(): void {
    if (this.vendorContactForm.invalid) {
      this.vendorContactForm.markAllAsTouched();
      this.toastr.warning(
        'Please fill all required fields marked with * before saving.',
        'Validation Error',
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const formValue = this.vendorContactForm.value;
    let contactName = formValue.FirstName;
    if (formValue.FirstName) {
      if (formValue.LastName) {
        contactName = [formValue.FirstName, formValue.LastName].join(' ').trim();
      } else if (formValue.MiddleName) {
        contactName = [formValue.FirstName, formValue.MiddleName].join(' ').trim();
      } else {
        contactName = formValue.FirstName.trim();
      }
    }

    const contactPerson = {
      ContactPersonID: Number(formValue.ContactPersonID) || 0,
      VendorId: Number(formValue.VendorId) || this.vendorId,
      ContactName: contactName,
      FirstName: formValue.FirstName?.trim() || '',
      MiddleName: formValue.MiddleName?.trim() || '',
      LastName: formValue.LastName?.trim() || '',
      Address: formValue.Address?.trim() || '',
      City: formValue.City?.trim(),
      StateCode: formValue.StateCode?.trim(),
      ZipCode: formValue.ZipCode?.trim(),
      CountryCode: Number(formValue.CountryCode) || 1,
      TelePhone: formValue.TelePhone?.trim() || '',
      EmailAddr: formValue.EmailAddr?.trim() || '',
      PhotoFile: this.isEditMode
        ? (this.isProfileImageUpdated ? formValue.PhotoFile || '' : '')
        : formValue.PhotoFile || '',
      ProfileImageName: formValue.ProfileImageName?.trim() || '',
      IsAuthorised: !!formValue.IsAuthorised,
      DeleteFlag: !!formValue.DeleteFlag,
      ImgDeleteFlag: formValue.ImgDeleteFlag || false
    };

    Swal.fire({
      title: $localize`Confirm Save`,
      text: $localize`Do you want to save this contact person?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, Save`,
      cancelButtonText: $localize`Cancel`,
      reverseButtons: true,
    }).then(result => {
      if (result.isConfirmed) {
        this.vendorService.saveVendorContactPerson(contactPerson)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: res => {
              if (res?.Success) {
                this.toastr.success(res?.Message || $localize`Contact person saved successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
                this.viewContactPerson = true;
                this.isProfileImageUpdated = false;
                this.logoPreviewUrl = '../assets/images/NoPhoto.svg';
                this.loadVendorContacts();
              } else {
                this.toastr.error(res?.Message || $localize`Failed to save contact person`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
              }
            },
            error: err => {
              console.error('Error saving contact person', err);
            }
          });
      }
    });
  }

  deleteVendorContactPerson(data: any): void {
    const formValue = this.vendorContactForm.value;
    const payload = {
      ...formValue,
      ContactPersonID: Number(data.ContactPersonID) || 0,
      VendorId: Number(formValue.VendorId) || this.vendorId,
      ContactName: [formValue.FirstName, formValue.MiddleName, formValue.LastName]
        .filter(Boolean).join(' ').trim() || '',
      DeleteFlag: true,
      ImgDeleteFlag: true
    };

    Swal.fire({
      title: $localize`Confirm Delete`,
      text: $localize`Are you sure you want to remove the contact ${data.ContactName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, Delete`,
      cancelButtonText: $localize`Cancel`,
      reverseButtons: true,
    }).then(result => {
      if (result.isConfirmed) {
        this.vendorService.saveVendorContactPerson(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: res => {
              if (res?.Success) {
                this.toastr.success(res?.Message || $localize`Contact person deleted successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
                this.loadVendorContacts();
              } else {
                this.toastr.error(res?.Message || $localize`Failed to delete contact person`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
              }
            },
            error: err => {
              console.error('Delete contact error:', err);
            }
          });
      }
    });
  }

  goBack() {
    this.close.emit();
  }

  createContactPerson() {
    this.isEditMode = false;
    this.logoPreviewUrl = '../assets/images/NoPhoto.svg'
    this.vendorContactForm.patchValue({ CountryCode: ECountrycode.US });
    this.loadStates(ECountrycode.US);
    this.selectedCountryCode = ECountrycode.US;
    this.setZipCodeValidator(ECountrycode.US);
    this.vendorContactForm.reset({
      ContactPersonID: null,
      ContactName: '',
      FirstName: '',
      MiddleName: '',
      LastName: '',
      Address: '',
      EmailAddr: '',
      PhotoFile: '',
      IsAuthorised: false,
      CountryCode: ECountrycode.US,
      ZipCode: '',
      TelePhone: '',
      City: ''
    });
    this.viewContactPerson = false;
  }

  setZipCodeValidator(countryCode: number) {
    this.commonCalculatorService.setZipCodeValidator(countryCode, this.vendorContactForm.get('ZipCode'));
  }

  closeCreateContactPerson() {
    this.viewContactPerson = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
